/**
 * Cartão de uma seção do Portal Kit (uma coluna ou um bloco): cabeçalho com o
 * estado, controles de liga e de quem serve, editor recolhível e o rodapé de
 * ações. Cuida do rascunho, da validação, da gravação com `If-Match` e dos três
 * desfechos que a pessoa precisa entender:
 *
 *  - salvou: aparece "Salvo" e a versão nova entra no cache;
 *  - 412: "outra pessoa alterou esta coluna; recarregue" (nada é perdido em
 *    silêncio: o rascunho fica até a pessoa pedir para recarregar);
 *  - 422: os campos que o servidor recusou aparecem na lista.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { Badge, Button } from '@/components/atoms';
import { useRestaurarSecao, useSalvarSecao, portalKeys } from '@/hooks/usePortal';
import { classificarFalha, errosDeCampo } from '@/services/portal';
import {
  ehColuna,
  type CorpoSecao,
  type ErroDeCampo,
  type OrigemConfig,
  type SecaoId,
  type ServidaPor,
} from '@/types/portal';

import { CampoInterruptor, CampoSelecao, ListaDeErros } from './campos';
import { iguais } from './helpers';
import styles from './Portal.module.css';

/** O que o cartão precisa de uma seção, seja coluna (com ligas) ou bloco (só config). */
export interface SecaoGenerica<C> {
  chave: SecaoId;
  versao: number;
  origem: OrigemConfig;
  config: C;
  ativa?: boolean;
  servida_por?: ServidaPor;
  sitemap?: boolean;
}

interface Rascunho<C> {
  ativa: boolean;
  servida_por: ServidaPor;
  sitemap: boolean;
  config: C;
}

function inicial<C>(s: SecaoGenerica<C>): Rascunho<C> {
  return {
    ativa: s.ativa ?? true,
    servida_por: s.servida_por ?? 'tema',
    sitemap: s.sitemap ?? false,
    config: s.config,
  };
}

const ROTULO_ORIGEM: Record<OrigemConfig, string> = {
  site: 'Configuração do site',
  tenant: 'Herdada do padrão do tenant',
  padrao: 'Padrão do produto',
};

export interface ContextoDoEditor<C> {
  config: C;
  mudar: (c: C) => void;
  erros: ErroDeCampo[];
}

interface Props<C> {
  siteId: string;
  secao: SecaoGenerica<C>;
  titulo: string;
  resumo?: string;
  validar: (config: C) => ErroDeCampo[];
  children: (ctx: ContextoDoEditor<C>) => ReactNode;
  abertoInicial?: boolean;
}

export function CartaoSecao<C>({
  siteId,
  secao,
  titulo,
  resumo,
  validar,
  children,
  abertoInicial = false,
}: Props<C>) {
  const qc = useQueryClient();
  const salvarMut = useSalvarSecao(siteId);
  const restaurarMut = useRestaurarSecao(siteId);
  const coluna = ehColuna(secao.chave);

  const [versaoBase, setVersaoBase] = useState(secao.versao);
  const [rascunho, setRascunho] = useState<Rascunho<C>>(() => inicial(secao));
  const [aberto, setAberto] = useState(abertoInicial);
  const [errosServidor, setErrosServidor] = useState<ErroDeCampo[]>([]);
  const [conflito, setConflito] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  // A seção mudou por fora (gravou, ou a pessoa recarregou depois de um 412):
  // o rascunho volta a ser o que o servidor tem. É o padrão da documentação do
  // React para "ajustar estado quando a prop muda", sem efeito e sem remontar.
  if (versaoBase !== secao.versao) {
    setVersaoBase(secao.versao);
    setRascunho(inicial(secao));
    setErrosServidor([]);
    setConflito(false);
    setFalha(null);
  }

  const original = inicial(secao);
  const sujo = !iguais(rascunho, original);
  const errosLocais = sujo ? validar(rascunho.config) : [];
  const erros = [...errosLocais, ...errosServidor];
  const ocupado = salvarMut.isPending || restaurarMut.isPending;

  const mudarConfig = (config: C) => {
    setRascunho((r) => ({ ...r, config }));
    setErrosServidor([]);
    setSalvo(false);
  };
  const mudarLiga = (parcial: Partial<Rascunho<C>>) => {
    setRascunho((r) => ({ ...r, ...parcial }));
    setSalvo(false);
  };

  const tratarFalha = (e: unknown) => {
    const tipo = classificarFalha(e);
    if (tipo === 'conflito') {
      setConflito(true);
    } else if (tipo === 'validacao') {
      const campos = errosDeCampo(e);
      setErrosServidor(
        campos.length > 0 ? campos : [{ campo: '', mensagem: 'O servidor recusou o conteúdo.' }],
      );
    } else {
      setFalha('Não foi possível salvar agora. Tente de novo em instantes.');
    }
  };

  const salvar = async () => {
    if (erros.length > 0 || !sujo) return;
    const viraParaPlugin =
      coluna &&
      rascunho.ativa &&
      rascunho.servida_por === 'plugin' &&
      original.servida_por !== 'plugin';
    if (
      viraParaPlugin &&
      !window.confirm(
        `Passar "${titulo}" para o plugin? O tema deixa de responder pelas URLs desta coluna na próxima sincronização (até 5 minutos). Para desfazer, volte para "Tema" aqui, sem deploy.`,
      )
    ) {
      return;
    }
    const corpo = (
      coluna
        ? {
            ativa: rascunho.ativa,
            servida_por: rascunho.servida_por,
            sitemap: rascunho.sitemap,
            config: rascunho.config,
          }
        : { config: rascunho.config }
    ) as CorpoSecao;
    setFalha(null);
    try {
      await salvarMut.mutateAsync({ chave: secao.chave, corpo, versao: secao.versao });
      setSalvo(true);
    } catch (e) {
      tratarFalha(e);
    }
  };

  const voltarAoPadrao = async () => {
    if (
      !window.confirm(
        `Voltar "${titulo}" ao padrão? A configuração própria deste site é apagada e vale o que o tenant ou o produto definem.`,
      )
    ) {
      return;
    }
    setFalha(null);
    try {
      await restaurarMut.mutateAsync({ chave: secao.chave, versao: secao.versao });
      setSalvo(true);
    } catch (e) {
      tratarFalha(e);
    }
  };

  const recarregar = async () => {
    await qc.refetchQueries({ queryKey: portalKeys.site(siteId) });
    setConflito(false);
  };

  const descartar = () => {
    setRascunho(original);
    setErrosServidor([]);
    setSalvo(false);
  };

  const situacao = !rascunho.ativa
    ? { tom: 'neutral' as const, texto: 'Desligada' }
    : rascunho.servida_por === 'plugin'
      ? { tom: 'ok' as const, texto: 'Servida pelo plugin' }
      : { tom: 'info' as const, texto: 'Servida pelo tema' };

  return (
    <section className={styles.cartao} aria-label={titulo}>
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>{titulo}</h3>
          {resumo && <p className={styles.cartaoResumo}>{resumo}</p>}
        </div>
        <div className={styles.cartaoEtiquetas}>
          {coluna && <Badge tone={situacao.tom}>{situacao.texto}</Badge>}
          <Badge tone="neutral" size="xs">
            {ROTULO_ORIGEM[secao.origem]}
          </Badge>
          {sujo && (
            <Badge tone="warn" size="xs">
              Alterações não salvas
            </Badge>
          )}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            aria-expanded={aberto}
            onClick={() => setAberto((a) => !a)}
          >
            {aberto ? 'Fechar' : 'Editar'}
          </Button>
        </div>
      </div>

      {coluna && (
        <div className={styles.cartaoCorpo}>
          <div className={styles.grade}>
            <CampoInterruptor
              rotulo="Coluna ativa neste site"
              valor={rascunho.ativa}
              onChange={(ativa) => mudarLiga({ ativa })}
              dica="Desligada, as páginas dela deixam de existir (404)."
            />
            <CampoSelecao<ServidaPor>
              rotulo="Quem serve esta coluna"
              valor={rascunho.servida_por}
              onChange={(servida_por) => mudarLiga({ servida_por })}
              opcoes={[
                { valor: 'tema', rotulo: 'Tema (como está hoje)' },
                { valor: 'plugin', rotulo: 'Plugin do WIREDESK' },
              ]}
              dica="Voltar para o tema desfaz a virada em até 5 minutos, sem deploy."
            />
          </div>
        </div>
      )}

      {aberto && (
        <div className={styles.cartaoCorpo}>
          {children({ config: rascunho.config, mudar: mudarConfig, erros })}
        </div>
      )}

      {(aberto || sujo || conflito || salvo || falha) && (
        <div className={styles.cartaoCorpo}>
          {conflito && (
            <div role="alert" className={styles.avisoWarn}>
              <span className={styles.avisoTitulo}>
                Outra pessoa alterou esta coluna; recarregue.
              </span>
              <span>
                Suas alterações ainda estão aqui, mas foram feitas sobre uma versão antiga. Ao
                recarregar, elas são descartadas e você vê o que está salvo.
              </span>
              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => void recarregar()}
                >
                  Recarregar
                </Button>
              </div>
            </div>
          )}
          <ListaDeErros erros={erros} />
          {falha && (
            <div role="alert" className={styles.avisoCrit}>
              {falha}
            </div>
          )}
          <div className={styles.cartaoRodape}>
            <Button
              type="button"
              size="sm"
              disabled={!sujo || erros.length > 0 || ocupado || conflito}
              loading={salvarMut.isPending}
              onClick={() => void salvar()}
            >
              Salvar
            </Button>
            {sujo && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={descartar}
                disabled={ocupado}
              >
                Descartar alterações
              </Button>
            )}
            {secao.origem === 'site' && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => void voltarAoPadrao()}
                disabled={ocupado}
              >
                Voltar ao padrão
              </Button>
            )}
            <span role="status" className={styles.mudo}>
              {salvo && !sujo ? 'Salvo.' : ''}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
